import { PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import { ArchiveIcon, Building2Icon, CalendarRangeIcon, LayoutDashboardIcon } from 'lucide-react';

import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { ProductSideNav, type ProductSideNavItem } from '@/platform/components/product-side-nav';
import { registerProductNav } from '@/platform/lib/product-nav';
import {
  MAINTENANCE_ARCHIVE_PATH,
  MAINTENANCE_CUSTOMERS_PATH,
  MAINTENANCE_PLANNING_PATH,
  MAINTENANCE_ROOT,
  MAINTENANCE_TEMPLATES_PATH,
  MAINTENANCE_TRASH_PATH,
} from '@/products/maintenance/maintenance-paths';

const iconClass = 'size-4';

function MaintenanceNav() {
  const me = useMe();
  const masterData = hasPermission(me.data, PermissionCode.MAINTENANCE_MASTER_DATA_MANAGE);
  const templates = hasPermission(me.data, PermissionCode.MAINTENANCE_TEMPLATES_MANAGE);
  const planning = hasPermission(me.data, PermissionCode.MAINTENANCE_ORDERS_MANAGE);
  const archive = hasPermission(me.data, PermissionCode.MAINTENANCE_PROTOCOLS_READ_ALL);

  const items: ProductSideNavItem[] = [
    {
      to: MAINTENANCE_ROOT,
      label: 'Übersicht',
      end: true,
      icon: <LayoutDashboardIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    ...(planning
      ? [
          {
            to: MAINTENANCE_PLANNING_PATH,
            label: 'Planung',
            icon: <CalendarRangeIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(archive
      ? [
          {
            to: MAINTENANCE_ARCHIVE_PATH,
            label: 'Archiv',
            icon: <ArchiveIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(planning ? [{ to: MAINTENANCE_TRASH_PATH, label: 'Papierkorb' }] : []),
    ...(masterData
      ? [
          {
            to: MAINTENANCE_CUSTOMERS_PATH,
            label: 'Kunden',
            icon: <Building2Icon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(templates ? [{ to: MAINTENANCE_TEMPLATES_PATH, label: 'Vorlagen' }] : []),
  ];

  return <ProductSideNav ariaLabel="Wartungsprotokolle" items={items} />;
}

registerProductNav({
  code: ProductCode.MAINTENANCE,
  Nav: MaintenanceNav,
  menuTiles: [
    { id: 'maintenance-home', label: 'Übersicht', to: MAINTENANCE_ROOT },
    {
      id: 'maintenance-planning',
      label: 'Planung',
      to: MAINTENANCE_PLANNING_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_ORDERS_MANAGE),
    },
    {
      id: 'maintenance-archive',
      label: 'Archiv',
      to: MAINTENANCE_ARCHIVE_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_PROTOCOLS_READ_ALL),
    },
    {
      id: 'maintenance-trash',
      label: 'Papierkorb',
      to: MAINTENANCE_TRASH_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_ORDERS_MANAGE),
    },
    {
      id: 'maintenance-customers',
      label: 'Kunden',
      to: MAINTENANCE_CUSTOMERS_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_MASTER_DATA_MANAGE),
    },
    {
      id: 'maintenance-templates',
      label: 'Vorlagen',
      to: MAINTENANCE_TEMPLATES_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_TEMPLATES_MANAGE),
    },
  ],
  mobileSlots: [
    { kind: 'link', id: 'maintenance-home', label: 'Übersicht', to: MAINTENANCE_ROOT },
    {
      kind: 'link',
      id: 'maintenance-planning',
      label: 'Planung',
      to: MAINTENANCE_PLANNING_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_ORDERS_MANAGE),
    },
    {
      kind: 'link',
      id: 'maintenance-archive',
      label: 'Archiv',
      to: MAINTENANCE_ARCHIVE_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.MAINTENANCE_PROTOCOLS_READ_ALL),
    },
  ],
});
